import type { TriggerConfig } from './types';

/** Wires whichever trigger a form is configured with and invokes `fire` exactly once. Returns a
 * cleanup function so a caller can tear down listeners if the form gets suppressed mid-flight
 * (e.g. another form on the page already converted this visitor). */
export function armTrigger(trigger: TriggerConfig, fire: () => void): () => void {
  let fired = false;
  const fireOnce = () => {
    if (fired) return;
    fired = true;
    fire();
  };

  switch (trigger.type) {
    case 'PAGE_LOAD': {
      // Deferred a tick so the DOM the widget renders into is guaranteed ready.
      const id = window.setTimeout(fireOnce, 0);
      return () => window.clearTimeout(id);
    }

    case 'TIME_DELAY': {
      const seconds = Number(trigger.value) || 0;
      const id = window.setTimeout(fireOnce, seconds * 1000);
      return () => window.clearTimeout(id);
    }

    case 'SCROLL_PERCENT': {
      const threshold = Number(trigger.value) || 0;
      const onScroll = () => {
        const doc = document.documentElement;
        const scrolled = doc.scrollHeight <= doc.clientHeight
          ? 100
          : (doc.scrollTop / (doc.scrollHeight - doc.clientHeight)) * 100;
        if (scrolled >= threshold) fireOnce();
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }

    case 'EXIT_INTENT': {
      const onMouseOut = (e: MouseEvent) => {
        if (e.clientY <= 0) fireOnce();
      };
      document.addEventListener('mouseout', onMouseOut);
      return () => document.removeEventListener('mouseout', onMouseOut);
    }

    case 'CLICK': {
      const selector = trigger.value;
      if (!selector) return () => {};
      const onClick = (e: Event) => {
        if ((e.target as Element)?.closest(selector)) fireOnce();
      };
      document.addEventListener('click', onClick);
      return () => document.removeEventListener('click', onClick);
    }

    default:
      return () => {};
  }
}
